import { AddToPhotos } from '@mui/icons-material';
import { Col, Progress, Row } from "antd";
import ModalMenu from "components/Modals";
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import React, { useEffect, useRef, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import styled from "styled-components";
import COLOR from "utils/constants/color";
import { imageUploader } from "utils/methods/imageUploader";
import { IMAGE_FILE_TYPE, UPLOAD_IMAGE_TO_FIREBASE } from "utils/constants/enum"


// Responsive container that adapts to different screen sizes
const ImageContainer = styled.div`
  height: auto;
  flex: 1;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 10px;
  }
`;

const Images = styled.img`
  background-color: ${COLOR.white};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);
  max-height: 100%;
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  
  @media (max-width: 768px) {
    max-height: 200px;
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: ${(p) => (p.empty ? "rgba(0,0,0,0.1)" : "transparent")};
`;

const Page = styled.div`
  align-items: center;
  color: ${COLOR.black100};
  display: flex;
  flex-direction: row;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  
  @media (max-width: 480px) {
    font-size: 12px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Input = styled.input`
  background-color: transparent;
  border: 1px dashed gray;
  border-radius: 3px;
  box-shadow: none;
  font-size: inherit;
  font-weight: 400;
  outline: none;
  padding: 0px 2px;
  transition: all 300ms ease;
  width: 250px;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-top: 5px;
  }
`;

const FileUploadArea = styled.div`
  position: relative;
  width: 100%;
  border: 1px dashed gray;
  padding: 5px 12px;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${COLOR.green};
    background-color: rgba(0, 0, 0, 0.03);
  }
`;

const Text = styled.div`
  font-size: 12px;
  
  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const Column = styled(Col)`
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 480px) {
    padding: 2px 0;
  }
`;

const ImageWrapper = styled.div`
    min-height: 300px;
  position: relative;
  border: 1px dashed;
  padding: 10px;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f9f9f9;
  padding-top: 23px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 8px;
    padding-top: 15px;
  }
`;

// Custom styled file uploader that responds to different screen sizes
const ResponsiveFileUploader = styled(FileUploader)`
  &.image-uploader-cover-starter {
    position: relative !important;
    width: 100%;
    margin-top: 15px;
    background: rgba(244, 239, 239, 0.86);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(244, 239, 239, 1);
    }
  }
`;

export default function ImageUploader({
  src,
  onChange,
  description,
  onChangeDescription,
  style,
  hasDescription,
  pageId,
  path,
  setImageCoverLoader = () => {},
}) {
  const showAlert = useAlert();
  const [pagePoster, setpagePoster] = useState(src);
  const [loader, setLoader] = useState(false);
  const [loadingMap, setLoadingMap] = useState(0);
  const [imageModal, setImageModal] = useState(false);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  const containerRef = useRef(null);

  // Add orientation change detection
  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setpagePoster(src);
  }, [src]);

  const handleChange = (file) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    uploadToStorage(file, pageId);
    reader.onloadend = function (e) {
      let v = reader.result;
      setpagePoster(v);
    };
  };

  const uploadToStorage = async (file, index) => {
    if (file) {
      try {
        setLoader(true);
        setImageCoverLoader(true);
        const onProgress = (per) => {
          setLoadingMap(per);
          setLoader(true);
          setImageCoverLoader(true);
        };
        const download_url = await imageUploader(file, onProgress, UPLOAD_IMAGE_TO_FIREBASE);
        if (download_url) {
          onChange({ url: download_url, pageId: index });
        } else {
          showAlert("Unable to upload image.", "error");
        }
      } catch (error) {
        console.error("Upload error:", error);
        showAlert("Unable to upload image.", "error");
      } finally {
        setLoader(false);
        setLoadingMap(0);
        setImageCoverLoader(false);
      }
    } else {
      console.log("No file to upload.");
    }
  };
  
  return (
    <ImageContainer 
      ref={containerRef} 
      onContextMenu={(e) => {}} 
      style={{
        ...style,
        maxHeight: isPortrait ? '70vh' : '50vh',
      }}
    >
      <ModalMenu
        type="imageModal"
        image={pagePoster}
        open={imageModal}
        close={() => setImageModal(false)}
      />
      
      {hasDescription && (
        <Row style={{ height: 'auto', marginBottom: '10px' }}>
          <Col span={24} style={{ display: 'flex', flexDirection: isPortrait ? 'column' : 'row' }}>
            <Page>
              description:&nbsp;
              <Input
                type="text"
                value={description || ""}
                onChange={(e) => onChangeDescription(e.target.value)}
              />
            </Page>
          </Col>
        </Row>
      )}
      
      <ImageWrapper empty={!pagePoster}>
        <Images
          src={pagePoster}
          className={"cursor-zoomIn"}
          onClick={() => setImageModal(true)}
          style={{ 
            maxHeight: isPortrait ? '200px' : '300px',
          }}
        />
        
        <ResponsiveFileUploader
          classes={"image-uploader-cover-starter"}
          handleChange={handleChange}
          name="file"
          types={IMAGE_FILE_TYPE}
        >
          <FileUploadArea>
            {loader && (
              <Progress
                type="line"
                percent={loadingMap}
                size={"small"}
                className={"fileProgress"}
                showInfo={false}
              />
            )}
            <Row
              className="box box-1"
              style={{ 
                display: 'flex', 
                flexDirection: isPortrait ? 'column' : 'row',
                justifyContent: isPortrait ? 'center' : 'space-between',
                alignItems: 'center',
                padding: '5px 0'
              }}
            >
              <Column xs={24} sm={isPortrait ? 24 : 8}>
                <AddToPhotos color="primary" />
              </Column>
              <Column xs={24} sm={isPortrait ? 24 : 8}>
                <Text>Upload or drop a file right here</Text>
              </Column>
              <Column xs={24} sm={isPortrait ? 24 : 8}>
                <Text>{IMAGE_FILE_TYPE.join(",")}</Text>
              </Column>
            </Row>
          </FileUploadArea>
        </ResponsiveFileUploader>
      </ImageWrapper>
    </ImageContainer>
  );
}